import { Injectable, OnDestroy } from '@angular/core';
import 'rxjs/add/operator/map';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/Rx';
import { UsersWebService } from "./users.web.service";
import {
	AccountType, ActiveDirectorySettingsDto, GoogleWorkspaceSettingsDto, LoginResultDto,
	LogoutResultDto, LogoutResultCode, UserClass, UserDto, UserPermissions
} from "UvExplorerServer.Dtos.TS/Auth";
import { SessionDto, SessionStateEntryDto } from "UvExplorerServer.Dtos.TS/Session";
import { CookieUtils } from 'common';
import { UvExplorerClient } from 'common';
import { Router } from '@angular/router';

@Injectable()
export class IdentityService implements OnDestroy {

	// How often to update user permissions
	private static readonly UserPermissionsTimeoutMs = 15 * 60 * 1000;	// 15 minutes

	// How long to continue using cached user permissions if they can't be updated (e.g., when the server is down)
	private static readonly UserPermissionsGracePeriodMs = 4 * IdentityService.UserPermissionsTimeoutMs;	// 1 hour

	// Needed to avoid circular dependency between IdentityService and NavigationService
	public static gotoNavDestination: () => void;
	public static gotoLogin: () => void;

	public user: UserDto;
	public session: SessionDto;
	public userPermissions: UserPermissions;

	private loginSubject: Subject<UserDto>;
	public onLogin: Observable<UserDto>;
	private logoutSubject: Subject<boolean>;
	public onLogout: Observable<boolean>;
	private userPermissionsChangedSubject: Subject<UserPermissions>;
	public onUserPermissionsChanged: Observable<UserPermissions>;

	public isReady: boolean;

	private userPermissionsIntervalId: any;

	constructor(private usersWebService: UsersWebService, private router: Router) {

		// Needed to avoid circular dependency between UvExplorerClient and IdentityService
		UvExplorerClient.clearSession = () => this.clearSession();

		this.user = null;
		this.session = null;
		this.userPermissions = null;

		this.loginSubject = new Subject<UserDto>();
		this.onLogin = this.loginSubject.asObservable();
		this.logoutSubject = new Subject<boolean>();
		this.onLogout = this.logoutSubject.asObservable();
		this.userPermissionsChangedSubject = new Subject<UserPermissions>();
		this.onUserPermissionsChanged = this.userPermissionsChangedSubject.asObservable();

		this.isReady = false;

		this.getSession();

		this.userPermissionsIntervalId =
			setInterval(() => this.refreshUserPermissions(), IdentityService.UserPermissionsTimeoutMs);
	}

	public ngOnDestroy(): void {

		clearInterval(this.userPermissionsIntervalId);
	}

	public get hasUser(): boolean {
		return !!this.user;
	}

	public get hasSession(): boolean {
		return !!this.session;
	}

	public get hasUserPermissions(): boolean {
		return !!this.userPermissions;
	}

	public get sessionGuid(): string {
		return CookieUtils.getCookie();
	}

	public get isAdmin(): boolean {
		if (!this.isLoggedIn) {
			return false;
		}
		else {
			return (this.session.EffectiveUserClass == UserClass.Administrator);
		}
	}

	public get isRegular(): boolean {
		if (!this.isLoggedIn) {
			return false;
		}
		else {
			return (this.session.EffectiveUserClass == UserClass.Regular);
		}
	}

	public get isObserver(): boolean {
		if (!this.isLoggedIn) {
			return false;
		}
		else {
			return (this.session.EffectiveUserClass == UserClass.Observer);
		}
	}

	public getAuthToken(): string {
		if (!this.isLoggedIn) {
			return "";
		}
		else {
			return this.session.Guid;
		}
	}

	public loginNativeUser(username: string, password: string): Observable<LoginResultDto> {
		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginNativeUser(username, password).subscribe(
			(result: LoginResultDto) => {
				if (result.HasUser && result.HasSession) {
					subject.next(result);

					this.user = result.User;
					this.session = result.Session;
					this.userPermissions = result.UserPermissions;
					CookieUtils.setCookie(result.Session.Guid);
					IdentityService.gotoNavDestination();

					this.loginSubject.next(result.User);
				}
				else {
					let message = "Invalid Login Credentials";
					console.error(message);
					subject.error(message);
				}
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public loginActiveDirectoryUser(username: string, password: string, domain: string): Observable<LoginResultDto> {
		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginActiveDirectoryUser(username, password, domain).subscribe(
			(result: LoginResultDto) => {
				if (result.HasUser && result.HasSession) {
					subject.next(result);

					this.user = result.User;
					this.session = result.Session;
					this.userPermissions = result.UserPermissions;
					CookieUtils.setCookie(result.Session.Guid);
					IdentityService.gotoNavDestination();

					this.loginSubject.next(result.User);
				}
				else {
					let message = "login failed";
					console.error(message);
					subject.error(message);
				}
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public loginGoogleWorkspaceUser(finalRedirectUrl: string, errorRedirectUrl: string): Observable<LoginResultDto> {
		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginGoogleWorkspaceUser(finalRedirectUrl, errorRedirectUrl).subscribe(
			(result: LoginResultDto) => {
				if (result.HasRedirectUrl) {
					subject.next(result);

					//location.replace(result.RedirectUrl);
					//window.top.location.replace(result.RedirectUrl);
				}
				else {
					let message = "login failed";
					console.error(message);
					subject.error(message);
				}
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public loginSamlUser(finalRedirectUrl: string, errorRedirectUrl: string): Observable<LoginResultDto> {
		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginSamlUser(finalRedirectUrl, errorRedirectUrl).subscribe(
			(result: LoginResultDto) => {
				if (result.HasRedirectUrl) {
					subject.next(result);

					location.replace(result.RedirectUrl);
					window.top.location.replace(result.RedirectUrl);
				}
				else {
					let message = "login failed";
					console.error(message);
					subject.error(message);
				}
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public loginActiveDirectoryAdmin(username: string, password: string, domain: string): Observable<LoginResultDto> {
		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginActiveDirectoryAdmin(username, password, domain).subscribe(
			(result: LoginResultDto) => {
				subject.next(result);
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public loginGoogleWorkspaceAdmin(finalRedirectUrl: string, errorRedirectUrl: string): Observable<LoginResultDto> {
		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginGoogleWorkspaceAdmin(finalRedirectUrl, errorRedirectUrl).subscribe(
			(result: LoginResultDto) => {
				if (result.HasRedirectUrl) {
					subject.next(result);

					location.replace(result.RedirectUrl);
				}
				else {
					let message = "login failed";
					console.error(message);
					subject.error(message);
				}
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public loginActiveDirectorySetAuthSettingsAdmin(adSettings: ActiveDirectorySettingsDto,
		username: string, password: string, domain: string): Observable<LoginResultDto> {

		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginActiveDirectorySetAuthSettingsAdmin(adSettings, username, password, domain).subscribe(
			(result: LoginResultDto) => {
				subject.next(result);
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public loginGoogleWorkspaceSetAuthSettingsAdmin(gwSettings: GoogleWorkspaceSettingsDto, finalRedirectUrl: string,
		errorRedirectUrl: string): Observable<LoginResultDto> {

		let subject = new Subject<LoginResultDto>();

		this.usersWebService.loginGoogleWorkspaceSetAuthSettingsAdmin(gwSettings, finalRedirectUrl, errorRedirectUrl).subscribe(
			(result: LoginResultDto) => {
				if (result.HasRedirectUrl) {
					subject.next(result);

					location.replace(result.RedirectUrl);
				}
				else {
					let message = "login failed";
					console.error(message);
					subject.error(message);
				}
			},
			(error: any) => {
				let message = "login failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	public testAdmin(accountType: AccountType): Observable<LoginResultDto> {
		return this.usersWebService.testAdmin(accountType);
	}

	public setSessionStateEntry(sessionStateEntry: SessionStateEntryDto): Observable<any> {

		return this.usersWebService.setSessionStateEntry(sessionStateEntry);
	}

	public getSessionStateEntry(entryKey: string): Observable<SessionStateEntryDto> {

		return this.usersWebService.getSessionStateEntry(entryKey);
	}

	public deleteSessionStateEntry(entryKey: string): Observable<any> {

		return this.usersWebService.deleteSessionStateEntry(entryKey);
	}

	private getSession(): void {
		let cookie = CookieUtils.getCookie();
		if (!!cookie) {
			this.usersWebService.getSession().subscribe(
				(result: LoginResultDto) => {
					this.user = result.User;
					this.session = result.Session;
					this.userPermissions = result.UserPermissions;
					CookieUtils.setCookie(result.Session.Guid);	// Shouldn't be necessary, but just to make sure
					this.isReady = true;
					IdentityService.gotoNavDestination();
					this.loginSubject.next(result.User);
				},
				(error: any) => {
					// TODO ????
					this.isReady = true;
					console.error("Session has expired. User needs to re-login.");
					this.logout();
				});
		}
		else {
			this.isReady = true;
		}
	}

	public logout(): Observable<LogoutResultDto> {
		let subject = new Subject<LogoutResultDto>();

		this.usersWebService.logoutUser().subscribe(
			(result: LogoutResultDto) => {
				this.clearSession();

				switch (result.Code) {
					case LogoutResultCode.Succeeded:
						subject.next(result);
						break;
					case LogoutResultCode.Redirect:
						subject.next(result);
						if (result.HasRedirectUrl) {
							location.replace(result.RedirectUrl);
						}
						break;
					case LogoutResultCode.Failed:
						{
							let message = "logout failed";
							console.error(message);
							subject.error(message);
						}
						break;
					default:
						console.assert(false);
						break;
				}
			},
			(error: any) => {
				this.clearSession();

				let message = "logout failed: " + error;
				console.error(message);
				subject.error(message);
			});

		return subject.asObservable();
	}

	private clearSession(): void {
		CookieUtils.clearCookie();
		this.user = null;
		this.session = null;
		this.userPermissions = null;
		this.userPermissionsChangedSubject.next(null);
		this.logoutSubject.next(true);
		IdentityService.gotoLogin();
	}

	public get isLoggedIn(): boolean {
		return (this.isReady && !!this.user);
	}

	private refreshUserPermissions() {
		if (this.isLoggedIn) {
			// Retrieve updated user permissions
			this.usersWebService.getUserPermissions().subscribe(
				(result: UserPermissions) => {
					this.userPermissions = result;
					this.userPermissionsChangedSubject.next(this.userPermissions);
				},
				(error) => {
					if (this.userPermissions) {
						// Purge user permissions that are too old.
						let now = new Date();
						let ageMs = Math.abs(now.getTime() - this.userPermissions.Timestamp.getTime());
						if (ageMs > IdentityService.UserPermissionsGracePeriodMs) {
							this.userPermissions = null;
							this.userPermissionsChangedSubject.next(null);
						}
					}
				}
			);
		}
	}

}
